// What a slash command's execution means for the composer — the pure half of
// SyncEngine.swift's `executeSlashCommand` (+ `SlashCommandOutcome`) and of
// ChannelViewModel.sendMessage's command branch (shared/API.md §15).
//
// `POST /api/v4/commands/execute` answers with a CommandResponse: `response_type` "ephemeral"
// means `text` is for THIS user only (the server also pushes it as an `ephemeral_message` over
// the socket); "in_channel" means the server already created a real post that arrives through
// the normal post pipeline. `goto_location` asks the client to go somewhere — the Apple app
// ignores it (it has no URLs); a browser client must honour it, because `/join` and `/msg`
// do nothing visible without it (web-only addition, the webapp's behaviour).

export type CommandOutcome =
  /**
   * Executed. `ephemeralText` non-null = show it to this user only. `triggerId` names the
   * interactive dialog this command may open (shared/API.md §15.6) — '' when none.
   */
  | { kind: 'executed'; ephemeralText: string | null; gotoLocation: string | null; triggerId: string }
  /** The server does not know the trigger (404). */
  | { kind: 'notFound'; message: string }
  /** Refused or broken; `message` is the server's own sentence ('' when it said nothing usable). */
  | { kind: 'failed'; message: string };

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** A successful response body → outcome (loose decode, like `SlashCommandResponse`). */
export function fromResponse(body: unknown): CommandOutcome {
  const o = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  const type = str(o.response_type);
  const text = str(o.text);
  // An empty `response_type` is ephemeral to the server (HandleCommandResponsePost).
  const ephemeral = (type === 'ephemeral' || type === '') && text.trim() !== '' ? text : null;
  const goto = str(o.goto_location).trim();
  return { kind: 'executed', ephemeralText: ephemeral, gotoLocation: goto === '' ? null : goto, triggerId: str(o.trigger_id) };
}

/** A refused execution → outcome. 404 is "unknown trigger" — everything else is a failure. */
export function fromError(status: number, message: string): CommandOutcome {
  if (status === 404) return { kind: 'notFound', message };
  return { kind: 'failed', message };
}

export type GotoTarget =
  /** A place inside this app: route it (`path` keeps its query). */
  | { kind: 'inApp'; path: string; search: string }
  /** Somewhere else: a new tab, never this one. */
  | { kind: 'external'; url: string };

function originOf(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.origin.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** The configured server's sub-path (`https://host/mattermost` → `/mattermost`), '' for none. */
function basePath(url: string): string {
  try {
    return new URL(url).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
}

/** Schemes a command's location may never use (shared/API.md §36.5). */
const UNSAFE_SCHEMES = ['javascript:', 'data:', 'vbscript:'];

/**
 * Where a `goto_location` goes (shared/API.md §36.5, in a command answer AND a post-action answer).
 * A relative path, or an absolute URL on this page's origin or on the server's SiteURL (the server
 * builds `/join`'s target from SiteURL, which is NOT this page's origin on the demo deployment), is
 * a place in this app; any other URL opens outside it (a new tab; `mailto:` and other apps through
 * the browser). `javascript:`, `data:` and `vbscript:` are refused — the location comes from a
 * command, which may be a third-party integration. A protocol-relative `//host` is refused too.
 */
export function gotoTarget(location: string, pageOrigin: string, siteUrl: string): GotoTarget | null {
  const raw = location.trim();
  if (raw === '') return null;
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    const q = raw.indexOf('?');
    const path = q < 0 ? raw : raw.slice(0, q);
    return { kind: 'inApp', path, search: q < 0 ? '' : raw.slice(q) };
  }
  const lower = raw.toLowerCase().replace(/\s+/g, '');
  if (UNSAFE_SCHEMES.some((s) => lower.startsWith(s))) return null;
  const origin = originOf(raw);
  if (!origin) {
    try {
      const other = new URL(raw);
      return other.protocol && other.protocol !== 'file:' ? { kind: 'external', url: other.toString() } : null;
    } catch {
      return null;
    }
  }
  const url = new URL(raw);
  const local = [pageOrigin, siteUrl].filter((s) => s !== '');
  for (const base of local) {
    if (originOf(base) !== origin) continue;
    const prefix = basePath(base);
    if (prefix && url.pathname !== prefix && !url.pathname.startsWith(prefix + '/')) continue;
    const path = prefix ? url.pathname.slice(prefix.length) || '/' : url.pathname;
    return { kind: 'inApp', path, search: url.search };
  }
  return { kind: 'external', url: url.toString() };
}

export const CommandOutcome = { fromResponse, fromError, gotoTarget } as const;
